import { AuthConfig, authExchange, AuthUtilities } from '@urql/exchange-auth'
import {
  cacheExchange,
  KeyingConfig,
  PossibleTypesConfig,
  UpdatesConfig,
} from '@urql/exchange-graphcache'
import { requestPolicyExchange } from '@urql/exchange-request-policy'
import { retryExchange } from '@urql/exchange-retry'
import {
  CombinedError,
  createClient,
  fetchExchange,
  mapExchange,
  Operation,
} from 'urql'

const isServerSide = typeof window === 'undefined'

export interface Authentication {
  accessToken: string
  refreshToken: string
  fingerprint: string
}

// global state
let authentication: Authentication | null = null

export function getAuthentication(): Authentication | null {
  return authentication
}

/**
 * Load authentication info from storage
 */
function loadAuthentication(): void {
  // SSR, no local storage on server
  if (isServerSide) return

  const accessToken = localStorage.getItem('accessToken')
  const refreshToken = localStorage.getItem('refreshToken')
  const fingerprint = localStorage.getItem('fingerprint')

  if (accessToken && refreshToken && fingerprint) {
    authentication = { accessToken, refreshToken, fingerprint }
  } else {
    authentication = null
  }
}

/**
 * Remove authentication info
 */
export function clearAuthentication() {
  authentication = null

  // SSR, no local storage on server
  if (isServerSide) return

  localStorage.removeItem('accessToken')
  localStorage.removeItem('refreshToken')
  localStorage.removeItem('fingerprint')
}

/**
 * Save authentication in memory and to storage
 */
export function saveAuthentication(auth: Authentication | null): void {
  authentication = auth

  if (!auth) clearAuthentication()
  else {
    // SSR, no local storage on server
    if (isServerSide) return

    localStorage.setItem('accessToken', auth.accessToken)
    localStorage.setItem('refreshToken', auth.refreshToken)
    localStorage.setItem('fingerprint', auth.fingerprint)
  }
}

function isPresent(auth: Authentication | null): auth is Authentication {
  return auth != null
}

function isAuthError(error: CombinedError) {
  return error.graphQLErrors.some(
    (e) => e.extensions.code == 'UNAUTHENTICATED_ERROR'
  )
}

function isOperationNeedToken(
  operation: Operation,
  anonymousFields: Set<string>
) {
  const isAnonymous = operation.query.definitions.some((d) => {
    if (d.kind != 'OperationDefinition') return false
    return d.selectionSet.selections.some((node) => {
      return node.kind === 'Field' && anonymousFields.has(node.name.value)
    })
  })
  return !isAnonymous
}

const TTL = 12 * 60 * 60 * 1000 // 12 hours
const SEEN_MAP = new Map<string, { seenAt: number; url: string }>()

function shouldRefresh(id: string, url: string) {
  const seen = SEEN_MAP.get(id)
  if (!seen || seen.url != url) {
    SEEN_MAP.set(id, { url, seenAt: Date.now() })
    return false
  }

  const expired = Date.now() - seen.seenAt >= TTL
  if (!expired) return false

  SEEN_MAP.delete(id)
  return true
}

function nullKey() {
  return null
}

export function withKeylessTypes(
  otherKeylessTypes: string[] = [],
  keys: KeyingConfig = {}
): KeyingConfig {
  const keylessTypes = ['PaginationInfo'].concat(otherKeylessTypes)
  return new Proxy(keys, {
    get(target, prop, receiver) {
      if (typeof prop == 'string') {
        if (prop.endsWith('Pagination') || keylessTypes.includes(prop))
          return nullKey
      }
      return Reflect.get(target, prop, receiver)
    },
  })
}

export type MutateFn = AuthUtilities['mutate']

export function makeClientStore(
  url: string,
  options: {
    isErrorHandledInBoundary: (error: unknown) => boolean
    cacheExchangeOptions: {
      possibleTypes: PossibleTypesConfig
      updates?: UpdatesConfig
      keys?: KeyingConfig
    }
    authExchangeOptions: {
      anonymousFields: Set<string>
      refreshAuth: (mutate: MutateFn) => Promise<void>
    }
  }
) {
  const exchanges = [
    requestPolicyExchange({
      ttl: 24 * 60 * 60 * 1000, // 1 day.
      shouldUpgrade: (op) => {
        // upgrades request policies to `cache-and-network` for query with @_autoRefresh directive
        return op.query.definitions.some((d) => {
          if (d.kind != 'OperationDefinition') return false
          return d.directives?.some(
            (directive) => directive.name.value == '_autoRefresh'
          )
        })
      },
    }),
    // only cache queries with @_nocache directive for 5s
    requestPolicyExchange({
      ttl: 5 * 1000, // 5 seconds.
      shouldUpgrade: (op) => {
        // upgrades request policies to `cache-and-network` for query with @_nocache directive
        return op.query.definitions.some((d) => {
          if (d.kind != 'OperationDefinition') return false
          return d.directives?.some(
            (directive) => directive.name.value == '_nocache'
          )
        })
      },
    }),
    cacheExchange({
      ...options.cacheExchangeOptions,
      globalIDs: true,
      resolvers: {
        UploadedFile: {
          url(parent) {
            const id = parent.id as string
            if (id && shouldRefresh(id, parent.url as string)) return undefined
            return parent.url
          },
        },
      },
    }),
    retryExchange({
      retryIf: (error, operation) =>
        !!error.networkError && operation.kind == 'query', // retry on query with network error
      randomDelay: true,
      initialDelayMs: 1000,
      maxDelayMs: 10000,
    }),
    mapExchange({
      onResult(result) {
        if (
          process.env.NODE_ENV === 'development' &&
          result.error &&
          options.isErrorHandledInBoundary(result.error)
        ) {
          // We will handle these errors in react error boundary.
          // But in dev mode, react will bubble error to window, trigger nextjs error overlay.
          // Thus, we will delete error stack trace to prevent error overlay in dev mode
          result.error.stack = undefined
        }
        return result
      },
    }),
    authExchange(async (utils) => {
      loadAuthentication()

      const authConfig: AuthConfig = {
        addAuthToOperation(operation) {
          const authentication = getAuthentication()
          if (!authentication?.accessToken) return operation

          return utils.appendHeaders(operation, {
            Authorization: `Bearer ${authentication.accessToken}`,
          })
        },
        willAuthError(operation) {
          const authentication = getAuthentication()
          return (
            !isServerSide &&
            !isPresent(authentication) &&
            isOperationNeedToken(
              operation,
              options.authExchangeOptions.anonymousFields
            )
          )
        },
        didAuthError(error) {
          const authentication = getAuthentication()
          return (
            !isServerSide && isPresent(authentication) && isAuthError(error)
          )
        },
        refreshAuth: () =>
          options.authExchangeOptions.refreshAuth(utils.mutate),
      }

      return authConfig
    }),
    fetchExchange,
  ]

  const makeClient = () =>
    createClient({
      url,
      suspense: true,
      preferGetMethod: false,
      fetchOptions: { credentials: 'include' },
      exchanges,
    })

  const clientStore = {
    current: makeClient(),
    subscribers: [] as Callback[],
    subscribe(cb: Callback) {
      this.subscribers.push(cb)
      return () =>
        (this.subscribers = this.subscribers.filter((sub) => sub != cb))
    },
    next() {
      this.current = makeClient()
      this.subscribers.forEach((cb) => cb())
    },
  }

  function getSnapshot() {
    return clientStore.current
  }

  function subscribe(cb: Callback) {
    return clientStore.subscribe(cb)
  }

  function resetClient() {
    clientStore.next()
  }

  return { getSnapshot, subscribe, resetClient }
}

type Callback = () => void
